import { useEffect, useRef, useState } from 'react';
import { Plus, Loader2, Trash2, Check, Pencil, Download, Upload, X, Save, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  parcelsApi,
  PARCEL_IMPORT_FIELDS,
  type ParcelImportProfile,
  type ParcelImportConfig,
  type ParcelImportField,
} from '../services/api';

const INP =
  'w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

const DEFAULT_CONFIG: ParcelImportConfig = {
  source: 'custom',
  fileFormat: 'csv',
  separator: ';',
  encoding: 'utf-8',
  decimalSeparator: '.',
  dateFormat: 'dd/MM/yyyy',
  hasHeader: true,
  startRow: 2,
  columnMapping: {},
};

const REQUIRED_KEYS: Array<keyof ParcelImportConfig> = [
  'source', 'fileFormat', 'separator', 'encoding', 'dateFormat', 'columnMapping',
];

/**
 * Per-source import profile management — mirrors the Fuel "Profils" tab:
 * card grid + create / import(JSON) / export(JSON) / edit / activate / delete.
 */
export default function ParcelProfilesTab() {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<ParcelImportProfile[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<ParcelImportProfile | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { profiles: list, activeProfileId } = await parcelsApi.listImportProfiles();
      setProfiles(list);
      setActiveId(activeProfileId);
    } catch (e) {
      setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.import.err'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportProfile = (p: ParcelImportProfile) => {
    const payload = { __type: 'trackfleet-parcel-profile', __version: 1, name: p.name, config: p.config };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parcel-profile-${p.name.replace(/[^a-zA-Z0-9_-]+/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const activate = async (id: string) => {
    try { await parcelsApi.activateImportProfile(id); await load(); }
    catch (e) { setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.import.err')); }
  };

  const remove = async (p: ParcelImportProfile) => {
    if (!window.confirm(t('parcels.import.confirm_delete_profile'))) return;
    try { await parcelsApi.deleteImportProfile(p.id); await load(); }
    catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'CANNOT_DELETE_LAST_PROFILE' ? t('parcels.import.cannot_delete_last') : t('parcels.import.err'));
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <div className="px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <Upload className="w-4 h-4" /> {t('parcels.import.import_profile')}
        </button>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white"
        >
          <Plus className="w-4 h-4" /> {t('parcels.import.new_profile')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading && <div className="col-span-2 text-center py-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        {!loading && profiles.length === 0 && (
          <div className="col-span-2 text-center py-6 text-gray-500">{t('parcels.import.empty_hint')}</div>
        )}
        {profiles.map((p) => (
          <div key={p.id} className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-medium text-gray-800 flex items-center gap-2">
                  {p.name}
                  {activeId === p.id && (
                    <span className="text-[10px] uppercase font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                      {t('parcels.import.active_badge')}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {p.config.source} · {p.config.fileFormat.toUpperCase()} · {t('parcels.import.separator')} "{p.config.separator}"
                </div>
              </div>
              <div className="flex gap-1">
                {activeId !== p.id && (
                  <button type="button" onClick={() => activate(p.id)} className="p-1 text-gray-500 hover:text-emerald-600" title={t('parcels.import.activate')}>
                    <Check className="w-4 h-4" />
                  </button>
                )}
                <button type="button" onClick={() => setEditing(p)} className="p-1 text-gray-500 hover:text-primary-600" title={t('parcels.edit')}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => exportProfile(p)} className="p-1 text-gray-500 hover:text-primary-600" title={t('parcels.import.export')}>
                  <Download className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => remove(p)} className="p-1 text-gray-500 hover:text-rose-600" title={t('parcels.import.delete_profile')}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {t('parcels.import.columns_mapped', { n: Object.keys(p.config.columnMapping || {}).length })}
            </div>
          </div>
        ))}
      </div>

      {createOpen && <ProfileCreateModal onClose={() => setCreateOpen(false)} onChange={load} />}
      {importOpen && <ProfileImportModal onClose={() => setImportOpen(false)} onChange={load} />}
      {editing && <ProfileEditModal profile={editing} onClose={() => setEditing(null)} onChange={load} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local UI helpers (mirror FuelPage's Modal / FileDropZone / Field)
// ---------------------------------------------------------------------------

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-content ${wide ? 'max-w-3xl' : 'max-w-xl'}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-gray-600 mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function FileDropZone({ file, onChange, accept, hint }: { file: File | null; onChange: (f: File | null) => void; accept: string; hint?: string }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const d = e.dataTransfer.files?.[0]; if (d) onChange(d); }}
      className={`block w-full rounded-lg border-2 border-dashed cursor-pointer transition-colors px-4 py-6 text-center ${
        dragOver ? 'border-primary-500 bg-primary-50' : file ? 'border-primary-300 bg-primary-50/40' : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50/30 bg-gray-50/50'
      }`}
    >
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => onChange(e.target.files?.[0] ?? null)} />
      <Upload className="w-5 h-5 mx-auto text-gray-400 mb-1" />
      <div className="text-sm text-gray-700">{file ? file.name : hint}</div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Create / Import / Edit modals
// ---------------------------------------------------------------------------

function ProfileCreateModal({ onClose, onChange }: { onClose: () => void; onChange: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await parcelsApi.createImportProfile(name.trim(), DEFAULT_CONFIG);
      onChange();
      onClose();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'P2002' ? t('parcels.import.name_exists') : t('parcels.import.err'));
    } finally { setBusy(false); }
  };

  return (
    <Modal title={t('parcels.import.new_profile')} onClose={onClose}>
      <div className="space-y-3">
        <Field label={t('parcels.import.profile_name')}>
          <input className={INP} value={name} onChange={(e) => setName(e.target.value)} placeholder="Colissimo" />
        </Field>
        <p className="text-xs text-gray-500">{t('parcels.import.create_hint')}</p>
        {error && <div className="text-sm text-rose-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">{t('parcels.import.cancel')}</button>
          <button type="button" onClick={submit} disabled={busy || !name.trim()} className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t('parcels.import.create')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ProfileImportModal({ onClose, onChange }: { onClose: () => void; onChange: () => void }) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<{ name: string; config: ParcelImportConfig } | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (f: File | null) => {
    setFile(f);
    setParsed(null);
    setError(null);
    if (!f) return;
    f.text()
      .then((text) => {
        const json = JSON.parse(text);
        if (json.__type !== 'trackfleet-parcel-profile' || !json.name || !json.config) {
          setError(t('parcels.import.invalid_file'));
          return;
        }
        for (const k of REQUIRED_KEYS) {
          if (json.config[k] === undefined) { setError(t('parcels.import.invalid_file')); return; }
        }
        setParsed({ name: json.name, config: { ...DEFAULT_CONFIG, ...json.config } });
        setName(json.name);
      })
      .catch(() => setError(t('parcels.import.json_unreadable')));
  };

  const submit = async () => {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      await parcelsApi.createImportProfile(name.trim() || parsed.name, parsed.config);
      onChange();
      onClose();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'P2002' ? t('parcels.import.name_exists') : t('parcels.import.err'));
    } finally { setBusy(false); }
  };

  return (
    <Modal title={t('parcels.import.import_profile')} onClose={onClose}>
      <div className="space-y-3">
        <FileDropZone file={file} onChange={handleFile} accept=".json,application/json" hint={t('parcels.import.json_hint')} />
        {parsed && (
          <>
            <Field label={t('parcels.import.rename_hint')}>
              <input className={INP} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <div className="text-xs text-gray-600 rounded-md bg-gray-50 border border-gray-200 px-2 py-1.5">
              {parsed.config.source} · {parsed.config.fileFormat.toUpperCase()} · {t('parcels.import.columns_mapped', { n: Object.keys(parsed.config.columnMapping || {}).length })}
            </div>
          </>
        )}
        {error && <div className="text-sm text-rose-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">{t('parcels.import.cancel')}</button>
          <button type="button" onClick={submit} disabled={!parsed || !name.trim() || busy} className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t('parcels.import.run')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ProfileEditModal({ profile, onClose, onChange }: { profile: ParcelImportProfile; onClose: () => void; onChange: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(profile.name);
  const [config, setConfig] = useState<ParcelImportConfig>({ ...DEFAULT_CONFIG, ...profile.config });
  const [headers, setHeaders] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setCfg = <K extends keyof ParcelImportConfig>(k: K, v: ParcelImportConfig[K]) => setConfig((c) => ({ ...c, [k]: v }));
  const setMapping = (field: ParcelImportField, ref: string) => {
    setConfig((c) => {
      const columnMapping = { ...c.columnMapping };
      if (ref) columnMapping[field] = { kind: 'column', ref };
      else delete columnMapping[field];
      return { ...c, columnMapping };
    });
  };

  const analyze = async (f: File) => {
    setBusy(true);
    setError(null);
    try {
      const res = await parcelsApi.inspectImport(f, {
        fileFormat: config.fileFormat, encoding: config.encoding, separator: config.separator,
        hasHeader: config.hasHeader, startRow: config.startRow,
      });
      setHeaders(res.headers);
    } catch (e) {
      setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.import.err'));
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await parcelsApi.updateImportProfile(profile.id, { name: name.trim() || undefined, config });
      onChange();
      onClose();
    } catch (e) {
      setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.import.err'));
    } finally { setBusy(false); }
  };

  return (
    <Modal title={profile.name} onClose={onClose} wide>
      <div className="space-y-4">
        <Field label={t('parcels.import.profile_name')}>
          <input className={INP} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <fieldset className="border border-gray-200 rounded-lg p-3">
          <legend className="px-1 text-xs font-semibold text-gray-500 uppercase">{t('parcels.import.options')}</legend>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Field label={t('parcels.import.format')}>
              <select className={INP} value={config.fileFormat} onChange={(e) => setCfg('fileFormat', e.target.value as ParcelImportConfig['fileFormat'])}>
                <option value="csv">CSV</option><option value="xlsx">XLSX</option>
              </select>
            </Field>
            <Field label={t('parcels.import.separator')}>
              <select className={INP} value={config.separator} onChange={(e) => setCfg('separator', e.target.value as ParcelImportConfig['separator'])} disabled={config.fileFormat === 'xlsx'}>
                <option value=";">;</option><option value=",">,</option><option value={'\t'}>Tab</option><option value="|">|</option>
              </select>
            </Field>
            <Field label={t('parcels.import.encoding')}>
              <select className={INP} value={config.encoding} onChange={(e) => setCfg('encoding', e.target.value as ParcelImportConfig['encoding'])} disabled={config.fileFormat === 'xlsx'}>
                <option value="utf-8">UTF-8</option><option value="utf-8-bom">UTF-8 (BOM)</option><option value="iso-8859-1">ISO-8859-1</option>
              </select>
            </Field>
            <Field label={t('parcels.import.date_format')}>
              <select className={INP} value={config.dateFormat} onChange={(e) => setCfg('dateFormat', e.target.value as ParcelImportConfig['dateFormat'])}>
                <option value="dd/MM/yyyy">dd/MM/yyyy</option><option value="yyyy-MM-dd">yyyy-MM-dd</option><option value="dd.MM.yyyy">dd.MM.yyyy</option><option value="MM/dd/yyyy">MM/dd/yyyy</option>
              </select>
            </Field>
            <Field label={t('parcels.import.start_row')}>
              <input type="number" min={1} className={INP} value={config.startRow} onChange={(e) => setCfg('startRow', Number(e.target.value) || 1)} />
            </Field>
            <Field label={t('parcels.import.has_header')}>
              <input type="checkbox" className="mt-2" checked={config.hasHeader} onChange={(e) => setCfg('hasHeader', e.target.checked)} />
            </Field>
          </div>
          <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <Upload className="w-4 h-4" /> {t('parcels.import.analyze_sample')}
            <input type="file" accept=".csv,.xlsx,.xls,.ods" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) analyze(f); e.target.value = ''; }} />
          </label>
          {headers.length > 0 && <span className="ml-2 text-xs text-gray-500">{t('parcels.import.headers_loaded', { n: headers.length })}</span>}
        </fieldset>

        <fieldset className="border border-gray-200 rounded-lg p-3">
          <legend className="px-1 text-xs font-semibold text-gray-500 uppercase">{t('parcels.import.mapping')}</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {PARCEL_IMPORT_FIELDS.map((field) => {
              const cur = config.columnMapping[field];
              const val = cur && cur.kind === 'column' ? String(cur.ref) : '';
              return (
                <Field key={field} label={t(`parcels.import.field.${field}`, field)}>
                  {headers.length > 0 ? (
                    <select className={INP} value={val} onChange={(e) => setMapping(field, e.target.value)}>
                      <option value="">— {t('parcels.import.none')} —</option>
                      {headers.map((h, i) => <option key={`${h}-${i}`} value={h}>{h || `Colonne ${i + 1}`}</option>)}
                    </select>
                  ) : (
                    <input className={INP} value={val} placeholder={t('parcels.import.column_name')} onChange={(e) => setMapping(field, e.target.value)} />
                  )}
                </Field>
              );
            })}
          </div>
        </fieldset>

        {error && <div className="text-sm text-rose-600">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">{t('parcels.import.cancel')}</button>
          <button type="button" onClick={save} disabled={busy} className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{t('parcels.import.save_profile')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
